import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Grant, RdsRegistration, api } from "../api";
import { useAuth } from "../auth";

function decodeJwtExp(token: string): number | null {
  const [, payload] = token.split(".");
  if (!payload) return null;
  try {
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof json.exp === "number" ? json.exp : null;
  } catch {
    return null;
  }
}

function formatRemaining(secs: number) {
  if (secs <= 0) return "expired";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function DashboardPage() {
  const { token, me, refresh } = useAuth();
  const [grants, setGrants] = useState<Grant[] | null>(null);
  const [rds, setRds] = useState<RdsRegistration[] | null>(null);
  const [now, setNow] = useState(Math.floor(Date.now() / 1000));
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 30_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!token) return;
    api.myGrants(token).then(setGrants).catch(() => setGrants([]));
    if (me?.is_admin) {
      api.listRds(token).then(setRds).catch(() => setRds([]));
    }
  }, [token, me?.is_admin]);

  const exp = useMemo(() => (token ? decodeJwtExp(token) : null), [token]);
  const remaining = exp ? exp - now : 0;

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await refresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="card p-5">
        <h2 className="text-lg font-semibold mb-4">Welcome, {me?.username}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <Stat label="Role" value={me?.is_admin ? "admin" : "analyst"} />
          <Stat
            label="Token expires in"
            value={exp ? formatRemaining(remaining) : "—"}
            subtle={exp ? new Date(exp * 1000).toLocaleString() : undefined}
          />
          <div>
            <button onClick={onRefresh} disabled={refreshing} className="btn-primary">
              {refreshing ? "Refreshing…" : "Refresh token"}
            </button>
            <p className="text-xs text-slate-500 mt-2">
              Mints a new JWT (calls <code>POST /api/auth/refresh</code>).
            </p>
          </div>
        </div>
      </section>

      <section className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">My access</h3>
          <Link to="/me/grants" className="text-sm text-slate-600 hover:text-slate-900">
            View →
          </Link>
        </div>
        {grants === null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : grants.length === 0 ? (
          <p className="text-sm text-slate-500">No grants. Ask an admin to assign you a schema.</p>
        ) : (
          <ul className="text-sm divide-y divide-slate-100">
            {grants.map((g) => (
              <li key={`${g.rds_id}:${g.schema_name}`} className="py-1.5 flex justify-between">
                <span>
                  RDS <code>#{g.rds_id}</code> · schema <code>{g.schema_name}</code>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {me?.is_admin && (
        <section className="card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold">Registered databases</h3>
            <Link to="/admin/databases" className="text-sm text-slate-600 hover:text-slate-900">
              Manage →
            </Link>
          </div>
          {rds === null ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : rds.length === 0 ? (
            <p className="text-sm text-slate-500">None registered.</p>
          ) : (
            <table className="table-clean">
              <thead>
                <tr>
                  <th>id</th>
                  <th>display_name</th>
                  <th>host:port</th>
                  <th>schemas</th>
                </tr>
              </thead>
              <tbody>
                {rds.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100">
                    <td className="font-mono">{r.id}</td>
                    <td>{r.display_name}</td>
                    <td className="text-xs text-slate-500">
                      {r.host}:{r.port}
                    </td>
                    <td className="text-xs">{r.schemas.join(", ") || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      )}
    </div>
  );
}

function Stat({ label, value, subtle }: { label: string; value: string; subtle?: string }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-base font-medium mt-1">{value}</div>
      {subtle && <div className="text-xs text-slate-400 mt-0.5">{subtle}</div>}
    </div>
  );
}
